import { Pipe, PipeTransform } from '@angular/core';

/**
 * Renderiza a formatação do WhatsApp: *negrito*, _itálico_, ~riscado~ e ```mono```.
 * Linhas compostas só de grupos [assim] (a convenção da transcrição para reply
 * buttons) viram chips de botão, como o WhatsApp exibe.
 * Escapa HTML antes de aplicar as marcações (seguro para [innerHTML]).
 */
@Pipe({ name: 'formataWhatsapp' })
export class FormataWhatsappPipe implements PipeTransform {
  transform(texto: string): string {
    if (!texto) return '';

    const escapado = texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const formatado = escapado
      .replace(/```([^`\n]+)```/g, '<code>$1</code>')
      .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_\n]+)_/g, '<em>$1</em>')
      .replace(/~([^~\n]+)~/g, '<s>$1</s>');

    // linha só de [botões] → chips (ex.: "[Ver cardápio] [Já sei o pedido]")
    const linhas = formatado.split('\n').map(linha => {
      // placeholder de mídia da transcrição (ex.: "📷 (foto)") → badge discreto
      const anexo = linha.match(/^\s*([^\w\s]\S{0,7})\s*\((foto|imagem|vídeo|video|áudio|audio|arquivo|figurinha)\)\s*$/i);
      if (anexo) return `<span class="wa-anexo">${anexo[1]} ${anexo[2].toLowerCase()}</span>`;

      const soBotoes = /^\s*(\[[^\[\]\n]+\]\s*)+$/.test(linha);
      if (!soBotoes) return linha;
      const chips = (linha.match(/\[([^\[\]]+)\]/g) || [])
        .map(grupo => `<span class="wa-botao">${grupo.slice(1, -1).trim()}</span>`)
        .join('');
      return `<span class="wa-botoes">${chips}</span>`;
    });

    // sequência longa de linhas curtas (ex.: categorias com emoji) vira bloco em colunas
    const compactado = this.agrupeLinhasCurtasEmColunas(linhas);

    // os <span> em bloco já quebram linha — remove os \n vizinhos p/ não duplicar
    return compactado.join('\n')
      .replace(/\n(?=<span class="wa-(botoes|colunas)[^"]*">)/g, '')
      .replace(/(<span class="wa-colunas[^"]*">[\s\S]*?<\/span>)\n/g, '$1');
  }

  /** Corridas de 6+ linhas curtas seguidas viram um bloco multi-coluna. */
  private agrupeLinhasCurtasEmColunas(linhas: string[]): string[] {
    const LINHAS_MINIMAS = 6;
    const TAMANHO_MAXIMO = 30;
    const curta = (linha: string) => {
      const textoPuro = linha.replace(/<[^>]+>/g, '').trim();
      return textoPuro.length > 0 && textoPuro.length <= TAMANHO_MAXIMO && !linha.includes('wa-botoes');
    };

    const resultado: string[] = [];
    let corrida: string[] = [];

    // Linha em branco antes/depois do bloco (ex.: lista numerada entre o título e a
    // pergunta) vira margem via classe: o \n vizinho do bloco é removido no final,
    // e um \n\n colado ao bloco não rende linha vazia no navegador — o respiro sumia.
    const descarregue = (blancoDepois = false) => {
      if (corrida.length >= LINHAS_MINIMAS) {
        const brancoAntes = resultado.length > 0 && resultado[resultado.length - 1].trim() === '';
        if (brancoAntes) resultado.pop();
        const classes = ['wa-colunas', brancoAntes ? 'wa-colunas--antes' : '', blancoDepois ? 'wa-colunas--depois' : '']
          .filter(Boolean).join(' ');
        resultado.push(`<span class="${classes}">${corrida.map(l => l.trim()).join('<br>')}</span>`);
        corrida = [];
        return true;
      }
      resultado.push(...corrida);
      corrida = [];
      return false;
    };

    for (const linha of linhas) {
      if (curta(linha)) { corrida.push(linha); continue; }
      const emBranco = linha.trim() === '';
      if (descarregue(emBranco) && emBranco) continue;
      resultado.push(linha);
    }
    descarregue();

    return resultado;
  }
}
